import React, {useContext} from 'react';
import {FormGroup, FormText} from 'reactstrap';
import {FormAbstractFieldProps, FormFieldProps} from '@src/@types/components/Form';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import trans from '@src/helpers/trans';
import FormErrorMessage from './FormErrorMessage';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import Translate from '@src/components/Translate/Translate';
import classnames from '@src/helpers/classnames';
import {availableFieldsTypes, getFieldComponentByType} from './getFieldComponentByType';
import {LocaleContext} from '@src/contexts/LocaleContext';
import usePermissionGranted from '@src/hooks/usePermissionGranted';
import FormFieldLabel from './FormFieldLabel';

const fieldTypesWithCustomLayout: (keyof typeof availableFieldsTypes)[] = ['switch', 'checkbox'];

export type Props<Model extends IEmptyModel> = FormAbstractFieldProps<Model, keyof typeof availableFieldsTypes> & {
    children?: React.ReactNode,
    fieldElement?: React.ComponentType<FormFieldProps<Model>>,
    labelElement?: React.ComponentType<Props<Model> & { labelId: string, labelText: string }>,
    renderLabelContent?: (props: Props<Model> & { labelId: string, labelText: string }) => React.ReactNode,
    withoutLayout?: boolean,
    tabName?: string | number,
};

export default function FormField<Model extends IEmptyModel>(props: Props<Model>) {
    const [design] = useContext(FormFieldDesignContext);
    const {locale} = useContext(LocaleContext);

    const {withoutLayout, fieldElement, labelElement, type, ...rest} = props;
    const id = rest.id ?? `${props.formName}@${props.fieldName}`;
    const label = rest.label ?? trans(props.fieldLabel, {}, null, locale);

    const canChangeField = usePermissionGranted(rest.config.permissionsToChange ?? []);
    const forceReadonly = rest.config.permissionsToChange != null && !canChangeField;

    let fieldJsx = props.children;
    if (!fieldJsx) {
        const Element = fieldElement ?? getFieldComponentByType(type);
        if (!Element) {
            return <Translate
                    message="Forms.errors.field_not_found"
                    tag="div"
                    className="alert alert-danger"
                    replace={{type}}
            />;
        }

        fieldJsx = <Element
                {...(rest as any)}
                id={id}
                label={label}
                forceReadonly={forceReadonly}
        />;
    }

    if (withoutLayout === true || fieldTypesWithCustomLayout.includes(type)) {
        return <>{fieldJsx}</>;
    }

    const LabelComponent = labelElement ?? FormFieldLabel;

    return <FormGroup
            className={classnames(design.wrapperClass, props.config.spanClass, {
                'd-none': props.config.hidden === true,
            })}
            {...(props.config.containerAttributes ?? {})}
    >
        {props.config.withoutLabel === true ? null : (
                <LabelComponent {...props} labelId={id} labelText={label}/>
        )}

        {fieldJsx}

        {props.config.comment ? <Translate message={props.config.comment} tag={FormText}/> : null}

        <FormErrorMessage fieldName={props.fieldName as string}/>
    </FormGroup>;
}
